import React, { useEffect, useState } from 'react';
import { useLoader } from '../../../../context/LoaderProvider.jsx';

const Fooduserwalletorders = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [pageData, setPageData] = useState([]);
    const [page, setPage] = useState(0);
    const [dateRange, setDateRange] = useState("yearly"); 
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [timeFilter, setTimeFilter] = useState(null);
    const [selectedYear, setSelectedYear] = useState("2025"); 
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [selectedCustomDates, setSelectedCustomDates] = useState("");

    const { showLoader, hideLoader } = useLoader();

    
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

  
    useEffect(() => {
        const today = formatDate(new Date());
      
        const year2025Start = "2025-01-01";
        const year2025End = "2025-12-31";
        setFromDate(year2025Start);
        setToDate(year2025End);
        setSelectedCustomDates(`${year2025Start} to ${year2025End}`);
    }, []);

   
    const formatDate = (date) => {
        return date.toISOString().split('T')[0];
    };

   
    const formatDisplayDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB'); 
    };

   
    const getDateRange = () => {
        const today = new Date();
        let startDate, endDate;

        switch (dateRange) {
            case 'today':
                startDate = today;
                endDate = today;
                break;
            case 'weekly':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 7);
                endDate = today;
                break;
            case 'monthly':
                startDate = new Date(today);
                startDate.setDate(1); 
                endDate = today;
                break;
            case 'yearly':
              
                const year = selectedYear ? parseInt(selectedYear) : today.getFullYear();
                startDate = new Date(year, 0, 1);
                endDate = new Date(year, 11, 31); 
                break;
            case 'custom':
                if (!fromDate || !toDate) {
                    alert("Please select both from and to dates for custom range");
                    return null;
                }
                startDate = new Date(fromDate);
                endDate = new Date(toDate);
                break;
            default:
                startDate = today;
                endDate = today;
        }

        return {
            fromDate: formatDate(startDate),
            toDate: formatDate(endDate)
        };
    };

    // ⭐ Fetch data from backend
    const fetchData = async () => {
        showLoader();
        try {
            const dateRangeObj = getDateRange();
            if (!dateRangeObj) {
                hideLoader();
                return;
            }

            const token = localStorage.getItem("authToken");
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const url = `https://backend.maamaas.com/food/api/ledger/company-restaurant/range?fromDate=${dateRangeObj.fromDate}&toDate=${dateRangeObj.toDate}`;

            const res = await fetch(url, {
                method: "GET",
                headers
            });

            const result = await res.json();

            if (Array.isArray(result)) {
                setData(result);
                setFilteredData(result); // Reset filteredData to new data
                setPage(0); // Reset to first page
                setSearchTerm(""); // Clear search term when new data arrives
            } else {
                console.error("Invalid data format:", result);
                setData([]);
                setFilteredData([]);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
            setData([]);
            setFilteredData([]);
        } finally {
            hideLoader();
        }
    };

   
    // Initial fetch - only run once on component mount
    useEffect(() => {
        fetchData();
    }, []);

   
    // ⭐ Fetch data when dateRange or time filters change
    useEffect(() => {
        fetchData();
    }, [dateRange, selectedYear, selectedMonth, selectedWeek]);

   
    // ⭐ Handle search filtering
    useEffect(() => {
        if (!data.length) return;

        let filtered = [...data];

        // Apply search filter
        if (searchTerm.trim() !== "") {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(item => {
                return (
                    (item.companyName && item.companyName.toLowerCase().includes(searchLower)) ||
                    (item.restaurantName && item.restaurantName.toLowerCase().includes(searchLower))
                );
            });
        }

        setFilteredData(filtered);
        setPage(0); // Reset to first page when search changes

        // Update page data for first page
        const startIndex = 0;
        const endIndex = startIndex + 10;
        setPageData(filtered.slice(startIndex, endIndex));

    }, [searchTerm]);

    // ⭐ Update pagination when page changes
    useEffect(() => {
        if (!filteredData.length) {
            setPageData([]);
            return;
        }
        
        const startIndex = page * 10;
        const endIndex = startIndex + 10;
        setPageData(filteredData.slice(startIndex, endIndex));
    }, [page, filteredData]);

    // ⭐ Pagination controls
    const goNext = () => {
        const totalPages = Math.ceil(filteredData.length / 10);
        if (page < totalPages - 1) {
            setPage(page + 1);
        }
    };

    const goPrevious = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };

    const getVisiblePages = () => {
        const totalPages = Math.ceil(filteredData.length / 10);

        if (totalPages <= 1) return [0];
        if (totalPages === 2) return [0, 1];
        if (totalPages === 3) return [0, 1, 2];

        if (page === 0) return [0, 1, 2];
        if (page === 1) return [0, 1, 2];
        if (page === totalPages - 1) return [page - 2, page - 1, page];
        return [page - 1, page, page + 1];
    };

    // ⭐ Export CSV
    const handleExport = () => {
        if (filteredData.length === 0) {
            alert("No data available for export.");
            return;
        }

        const headers = ["S.NO", "Company Name", "Restaurant Name", "Total Amount"];

        const csvRows = filteredData.map((item, index) => [
            index + 1,
            item.companyName || "-",
            item.restaurantName || "-",
            item.totalAmount ? `₹${Math.round(item.totalAmount)}` : "-"
        ]);

        const csvString = [headers.join(","), ...csvRows.map(r => r.join(","))].join("\r\n");

        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "company_restaurant_data.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    // ⭐ Apply custom date filter
    const handleCustomDateFilter = () => {
        if (!fromDate || !toDate) {
            alert("Please select both from and to dates");
            return;
        }

        const from = new Date(fromDate);
        const to = new Date(toDate);

        if (from > to) {
            alert("From date cannot be after to date");
            return;
        }

        setDateRange("custom");
        setSelectedCustomDates(`${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}`);
        fetchData();
    };

    // Helper function to show time filter
    const showTimeFilter = (filterType) => {
        setTimeFilter(filterType);
        setShowDatePicker(false);

        // Set default selections
        const today = new Date();
        if (filterType === 'yearly') {
            setSelectedYear(today.getFullYear().toString());
        } else if (filterType === 'monthly') {
            setSelectedMonth((today.getMonth() + 1).toString().padStart(2, '0'));
        } else if (filterType === 'weekly') {
            setSelectedWeek('This Week');
        }
    };

    // Function to handle year selection
    const selectYear = (year) => {
        setSelectedYear(year);
        setSelectedMonth(null);
        setSelectedWeek(null);

        setDateRange("yearly");
        setTimeFilter(null); // CLOSE popup

        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;

        setFromDate(startDate);
        setToDate(endDate);

        fetchData();
    };


    // Function to handle month selection
    const selectMonth = (month) => {
        setSelectedMonth(month);
        setSelectedYear(null);
        setSelectedWeek(null);

        setDateRange("monthly");
        setTimeFilter(null);

        const year = new Date().getFullYear();
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${lastDay}`;

        setFromDate(startDate);
        setToDate(endDate);

        fetchData();
    };


    // Function to handle week selection
    const selectWeek = (week) => {
        setSelectedWeek(week);
        setSelectedYear(null);
        setSelectedMonth(null);

        setDateRange("weekly");
        setTimeFilter(null);

        const today = new Date();
        let startDate, endDate;

        if (week === 'This Week') {
            const day = today.getDay();
            const diff = today.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(today.setDate(diff));
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);

            startDate = monday.toISOString().split('T')[0];
            endDate = sunday.toISOString().split('T')[0];
        } else if (week === 'Last Week') {
            const lastWeek = new Date();
            lastWeek.setDate(today.getDate() - 7);
            const day = lastWeek.getDay();
            const diff = lastWeek.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(lastWeek.setDate(diff));
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);

            startDate = monday.toISOString().split('T')[0];
            endDate = sunday.toISOString().split('T')[0];
        }

        setFromDate(startDate);
        setToDate(endDate);

        fetchData();
    };


    // Add click outside handler for popups
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (timeFilter && !event.target.closest('.time-filter-container')) {
                setTimeFilter(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [timeFilter]);

    // Get days difference for custom range
    const getDaysDifference = (start, end) => {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const timeDiff = endDate.getTime() - startDate.getTime();
        return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
    };

    // Reset function
    const handleReset = () => {
        setDateRange('yearly'); // Changed from 'today' to 'yearly'
        const year2025Start = "2025-01-01";
        const year2025End = "2025-12-31";
        setFromDate(year2025Start);
        setToDate(year2025End);
        setSearchTerm("");
        setPage(0);
        setShowDatePicker(false);
        setTimeFilter(null);
        setSelectedYear("2025"); // Reset to 2025
        setSelectedMonth(null);
        setSelectedWeek(null);
        setSelectedCustomDates(`${year2025Start} to ${year2025End}`);
        if (isMobile) setShowMobileMenu(false);
        fetchData();
    };

    // Get month name from month number
    const getMonthName = (monthNumber) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return months[parseInt(monthNumber) - 1];
    };

    // Get button label based on selection
    const getButtonLabel = (rangeKey) => {
        // If this is the currently active filter, show selection if exists
        if (dateRange === rangeKey) {
            switch (rangeKey) {
                case 'yearly':
                    return selectedYear ? `${selectedYear}` : `📅 ${isMobile ? 'Year' : 'Yearly'}`;
                case 'monthly':
                    return selectedMonth ? `${getMonthName(selectedMonth)}` : `📅 ${isMobile ? 'Month' : 'Monthly'}`;
                case 'weekly':
                    return selectedWeek ? `${selectedWeek}` : `📅 ${isMobile ? 'Week' : 'Weekly'}`;
                case 'custom':
                    return selectedCustomDates ? `${selectedCustomDates}` : `📅 ${isMobile ? 'Custom' : 'Custom Range'}`;
                default:
                    return `📅 ${isMobile ?
                        (rangeKey === 'yearly' ? 'Year' :
                            rangeKey === 'monthly' ? 'Month' :
                                rangeKey === 'weekly' ? 'Week' : 'Custom') :
                        (rangeKey === 'yearly' ? 'Yearly' :
                            rangeKey === 'monthly' ? 'Monthly' :
                                rangeKey === 'weekly' ? 'Weekly' : 'Custom Range')}`;
            }
        } else {
            // If not active, just show the basic label with emoji
            return `📅 ${isMobile ?
                (rangeKey === 'yearly' ? 'Year' :
                    rangeKey === 'monthly' ? 'Month' :
                        rangeKey === 'weekly' ? 'Week' : 'Custom') :
                (rangeKey === 'yearly' ? 'Yearly' :
                    rangeKey === 'monthly' ? 'Monthly' :
                        rangeKey === 'weekly' ? 'Weekly' : 'Custom Range')}`;
        }
    };

    return (
        <div style={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                // background: 'white',
                padding: isMobile ? '16px 20px' : '20px 28px',
                top: '-20px',
                zIndex: 100,
                marginTop: '-20px',
                flexShrink: 0
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                }}>
                   
                </div>

                {/* Time Range + Export/Reset */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '12px' : '16px',
                    alignItems: 'center',
                    top: '-21px',
                    justifyContent: 'space-between',
                    position: 'relative'
                }}>
                    {/* Time Range Selector */}
                    <div style={{
                        display: 'flex',
                        gap: isMobile ? '6px' : '15px',
                        background: 'transparent',
                        padding: isMobile ? '4px' : '6px',
                        borderRadius: '16px',
                        width: isMobile ? '100%' : 'auto',
                        flexWrap: 'wrap',
                        position: 'relative',
                        alignItems: 'center'
                    }}>
                        {/* Only Custom button remains */}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => {
                                    setDateRange('custom');
                                    setShowDatePicker(!showDatePicker);
                                    setTimeFilter(null);
                                }}
                                style={{
                                    padding: isMobile ? '8px 12px' : '10px 10px',
                                    background: dateRange === 'custom' ?
                                        'linear-gradient(135deg, #ef4444 0%, #ef444480 100%)' :
                                        'white',
                                    color: dateRange === 'custom' ? 'white' : '#ef4444',
                                    border: `2px solid ${dateRange === 'custom' ?
                                        '#ef4444' :
                                        '#ef444420'}`,
                                    borderRadius: '12px',
                                    fontSize: isMobile ? '12px' : '14px',
                                    fontWeight: dateRange === 'custom' ? '700' : '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    whiteSpace: 'nowrap',
                                    flex: isMobile ? '1' : 'none',
                                    position: 'relative',
                                    zIndex: 1,
                                    transition: 'all 0.3s ease',
                                    boxShadow: dateRange === 'custom' ?
                                        '0 4px 12px #ef444440' :
                                        '0 2px 8px rgba(0,0,0,0.05)',
                                    minWidth: isMobile ? '80px' : '100px',
                                    justifyContent: 'center',
                                    minHeight: '42px'
                                }}
                            >
                                {getButtonLabel('custom')}
                            </button>
                        </div>

                        {/* Search Bar - Added to the right of custom button */}
                        <input
                            type="text"
                            placeholder="Search by Company or Restaurant..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(0);
                            }}
                            style={{
                                backgroundColor: "#f8f9fa",
                                color: "#333",
                                border: "1px solid #ced4da",
                                height: "35px",
                                borderRadius: "4px",
                                padding: "0 10px",
                                width: isMobile ? "100%" : "250px",
                                marginLeft: "auto",
                                order: isMobile ? 5 : 'unset',
                                marginTop: isMobile ? '10px' : '0',
                                flex: isMobile ? '0 0 100%' : 'none'
                            }}
                        />
                    </div>

                    {/* Date Picker Modal with Today Button */}
                    {showDatePicker && (
                        <div style={{
                            position: 'absolute',
                            top: 'calc(100% + 3px)',
                            left: isMobile ? '16px' : '0px',
                            right: isMobile ? '16px' : 'calc(100% - 320px)',
                            background: 'white',
                            padding: '12px',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
                            zIndex: 1001,
                            width: isMobile ? 'calc(100% - 32px)' : '280px'
                        }} className='moblie-view-res' >
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                marginBottom: '12px',
                                flexWrap: 'nowrap'
                            }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '6px 8px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                                <span style={{
                                    color: '#94a3b8',
                                    fontSize: '11px',
                                    flexShrink: 0,
                                    minWidth: '20px',
                                    textAlign: 'center'
                                }}>
                                    to
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '6px 8px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                marginBottom: '12px',
                                flexWrap: 'wrap'
                            }}>
                                <button
                                    onClick={() => {
                                        const today = new Date();
                                        const todayStr = today.toISOString().split('T')[0];
                                        setFromDate(todayStr);
                                        setToDate(todayStr);
                                    }}
                                    style={{
                                        flex: '1 1 100px',
                                        padding: '6px 8px',
                                        background: '#f1f5f9',
                                        color: '#3b82f6',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        minWidth: 0
                                    }}
                                >
                                    ⏰ Today
                                </button>

                                {fromDate && toDate && (
                                    <div style={{
                                        flex: '1 1 100px',
                                        padding: '6px 8px',
                                        background: '#f8fafc',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        color: '#64748b',
                                        textAlign: 'center',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: 0
                                    }}>
                                        {getDaysDifference(fromDate, toDate)} days
                                    </div>
                                )}
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: '8px'
                            }}>
                                <button
                                    onClick={() => {
                                        handleCustomDateFilter();
                                        setShowDatePicker(false);
                                    }}
                                    disabled={!fromDate || !toDate}
                                    style={{
                                        flex: 1,
                                        padding: '6px',
                                        background: '#b15cd6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: (!fromDate || !toDate) ? 'not-allowed' : 'pointer',
                                        opacity: (!fromDate || !toDate) ? 0.5 : 1,
                                        minWidth: 0
                                    }}
                                >
                                    Apply
                                </button>
                                <button
                                    onClick={() => setShowDatePicker(false)}
                                    style={{
                                        flex: 1,
                                        padding: '6px',
                                        background: 'transparent',
                                        color: '#64748b',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        minWidth: 0
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Export and Reset Buttons */}
                    <div style={{ display: 'flex', gap: '8px', width: isMobile ? '100%' : 'auto' }} className='moblie-view-res1' >
                        <button
                            onClick={handleReset}
                            style={{
                                padding: isMobile ? '10px 16px' : '12px 20px',
                                background: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: isMobile ? '13px' : '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flex: isMobile ? 1 : 'none',
                                justifyContent: 'center'
                            }}
                        >
                            🔄 Reset
                        </button>
                        <button
                            onClick={handleExport}
                            style={{
                                padding: isMobile ? '10px 16px' : '12px 20px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: isMobile ? '13px' : '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flex: isMobile ? 1 : 'none',
                                justifyContent: 'center'
                            }}
                        >
                            📥 Export
                        </button>
                    </div>
                </div>


            </div>

            {/* Content Area */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                marginBottom: '70px',
                padding: isMobile ? '20px' : '28px',
                position: 'relative',
                marginTop: "-60px",
                WebkitOverflowScrolling: 'touch'
            }}>
                {/* TABLE */}
                <div className="table-responsive">
                    <table className="table table-bordered table-striped align-middle">
                        <thead>
                            <tr>
                                <th style={{ backgroundColor: "#b15cd6", color: "#fff", textAlign: "center", padding: "8px", border: "1px solid #ddd", fontSize: "14px" }}>S.NO</th>
                                <th style={{ backgroundColor: "#b15cd6", color: "#fff", textAlign: "center", padding: "8px", border: "1px solid #ddd", fontSize: "14px" }}>Company Name</th>
                                <th style={{ backgroundColor: "#b15cd6", color: "#fff", textAlign: "center", padding: "8px", border: "1px solid #ddd", fontSize: "14px" }}>Restaurant Name</th>
                                <th style={{ backgroundColor: "#b15cd6", color: "#fff", textAlign: "center", padding: "8px", border: "1px solid #ddd", fontSize: "14px" }}>Total Amount (₹)</th>
                            </tr>
                        </thead>

                        <tbody>
                            {pageData.length > 0 ? (
                                pageData.map((item, index) => (
                                    <tr key={index}>
                                        <td style={{ textAlign: "center", padding: "8px", border: "1px solid #ddd", fontSize: "14px" }}>{page * 10 + index + 1}</td>
                                        <td style={{ textAlign: "center", padding: "8px", border: "1px solid #ddd", fontSize: "14px" }}>
                                            <span style={{
                                                color: "#b15cd6",
                                                fontWeight: "bold"
                                            }}>
                                                {item.companyName || "-"}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: "center", padding: "8px", border: "1px solid #ddd", fontSize: "14px" }}>{item.restaurantName || "-"}</td>
                                        <td style={{ textAlign: "center", padding: "8px", border: "1px solid #ddd", fontSize: "14px" }}>
                                            <span style={{
                                                color: "#28a745",
                                                fontWeight: "bold"
                                            }}>
                                                ₹{item.totalAmount ? Math.round(item.totalAmount) : "-"}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                        {data.length > 0 && searchTerm.trim() !== "" 
                                            ? "No matching results found." 
                                            : "No data available for the selected date range."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {filteredData.length > 10 && (
                    <div className="d-flex justify-content-center mt-3" style={{ gap: "10px" }}>
                        <button
                            disabled={page === 0}
                            onClick={goPrevious}
                            className="btn btn-primary"
                        >
                            Previous
                        </button>

                        {getVisiblePages().map((p) => (
                            <button
                                key={p}
                                onClick={() => setPage(p)}
                                className={`btn ${page === p ? "btn-primary" : "btn-outline-primary"}`}
                            >
                                {p + 1}
                            </button>
                        ))}

                        <button
                            disabled={page >= Math.ceil(filteredData.length / 10) - 1}
                            onClick={goNext}
                            className="btn btn-primary"
                        >
                            Next
                        </button>
                    </div>
                )}

                {/* Page Info */}
               

                {/* Bottom padding */}
                <div style={{ height: isMobile ? '100px' : '60px' }}></div>
            </div>

            {/* Mobile Menu */}
            {showMobileMenu && isMobile && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end'
                }}>
                    <div style={{
                        width: '70%',
                        height: '100%',
                        background: 'white',
                        padding: '20px',
                        overflowY: 'auto'
                    }}>
                        {/* Mobile menu content */}
                        <button
                            onClick={() => setShowMobileMenu(false)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                color: '#333',
                                cursor: 'pointer',
                                marginBottom: '20px'
                            }}
                        >
                            ✕
                        </button>
                        {/* Add mobile menu items here */}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                ::-webkit-scrollbar { display: none; }
            `}</style>
        </div>
    );
};

export default Fooduserwalletorders;